import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Outlet, Link, useLocation } from 'react-router-dom';

import * as Icons from '@ant-design/icons';
import MyHeader from '@components/Header/Header';
import { Permission } from '@models/permission';
import { RootState } from '@store/index';
import { newMenuMap } from '@store/reducers/permissionSlice';
import { Layout, Menu } from 'antd';

import type { MenuProps } from 'antd';

import './DashboardLayout.scss';

type MenuItem = Required<MenuProps>['items'][number];

const { Content, Footer, Sider } = Layout;

function getItem(
  label: React.ReactNode,
  key: React.Key,
  path?: string,
  icon?: React.ReactNode | null,
  children?: MenuItem[],
  type?: 'group',
): MenuItem {
  return {
    label,
    key,
    path,
    icon,
    children,
    type,
  } as MenuItem;
}

type IconName = keyof typeof Icons;

function generateMenuItems(permissions: Permission[]): MenuItem[] {
  return permissions
    .flatMap((permission) => {
      const childrenItems = permission.children
        ? generateMenuItems(permission.children)
        : [];

      const title =
        permission.type === 'THIRD_LEVEL_MENU' ? (
          // 第三級菜單才有 path，才需要 Link
          <Link to={permission.path as string}>{permission.title}</Link>
        ) : (
          permission.title
        );

      // 第二級菜單只是一個左上角的群組標題，在 antd menu type 視為 group
      const itemType =
        permission.type === 'SECOND_LEVEL_MENU' ? 'group' : undefined;

      let icon = null;

      // 第一級菜單才有 icon，為了不一一引入 antd icon，所以這樣動態引入
      // 所以在頁面階層設定裡 icon 如果輸入了 antd 沒有的 icon，網頁會直接壞掉
      if (permission.icon) {
        const IconComponent = Icons[
          (permission.icon ?? 'ExclamationCircleOutlined') as IconName
        ] as React.ComponentType;
        icon = <IconComponent />;
      }

      // 經過上面的 getItem func 組成符合的格式
      return getItem(
        title,
        permission.name,
        permission.path,
        icon,
        permission.type !== 'THIRD_LEVEL_MENU' ? childrenItems : undefined, // 非第三級菜單項目的子項目
        itemType, // 菜單項目的類型，可能為 'group' 或 undefined
      );
    })
    .filter((item) => item); // 過濾掉所有未定義的項目（即那些跳過的菜單項目）
}

function DashboardLayout() {
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [backgroundImgClass, setBackgroundImgClass] = useState<string>(
    'Dashboard Dashboard--background-0 DashboardLayout',
  );
  const menuMap = useSelector(newMenuMap);
  const menuItems = generateMenuItems(menuMap);
  const { category, view } = useSelector(
    (state: RootState) => state.dashboardBackgroundImgSlice,
  );

  console.log('menuMap:', menuMap);
  console.log('menuItems:', menuItems);

  const selectedKeys = [location.pathname.split('/').pop()] as string[];

  function changeBackgroundImageHandler() {
    const defaultBackgroundImageClass = 'background-container';
    enum ECategoryState {
      'Home.EnergyDispatchSecurity' = 0,
      'Home.ChargingEquipmentStability' = 1,
      'Home.FinancialProfitabilityEfficiency' = 2,
    }

    enum EViewState {
      'immediate' = 0,
      'longTerm' = 1,
    }

    const backgroundImageClasses = [
      [
        'background-container--background-0-0',
        'background-container--background-0-1',
      ],
      [
        'background-container--background-1-0',
        'background-container--background-1-1',
      ],
      [
        'background-container--background-2-0',
        'background-container--background-2-1',
      ],
    ];

    console.log('category:', category);
    console.log('view:', view);
    const backgroundImageClass = `${defaultBackgroundImageClass} ${
      !category
        ? 'background-container--background-0'
        : backgroundImageClasses[ECategoryState[category]][EViewState[view]]
    }`;
    return backgroundImageClass;
  }

  useEffect(() => {
    setBackgroundImgClass(
      (prevBackgroundImgClass) =>
        `${prevBackgroundImgClass} background-container--fade-out`,
    );
    setTimeout(() => {
      setBackgroundImgClass(
        `${changeBackgroundImageHandler()} background-container--fade-in`,
      );
    }, 1000);
  }, [category, view]);

  return (
    <Layout
      className={`DashboardLayout ${
        location.pathname === '/Help/QuickProcess/CreateNewChargingStation'
          ? 'QuickProcessLayout'
          : ''
      }`}
      hasSider
    >
      <Sider
        // collapsible
        collapsed={collapsed}
        onCollapse={(value) => setCollapsed(value)}
        className="DashboardLayout__sider"
        style={{
          overflow: 'auto',
          height: '100vh',
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
          zIndex: 1,
        }}
      >
        <div className="DashboardLayout__logo">
          <Link to="/">星舟超充管理平台</Link>
        </div>
        <Menu mode="vertical" items={menuItems} selectedKeys={selectedKeys} />
      </Sider>
      <Layout style={{ marginLeft: 200 }}>
        <div className="preload-images" />
        <MyHeader />
        <Content className="DashboardLayout__content">
          {location.pathname !== '/home' ? null : (
            <section className={backgroundImgClass} />
          )}
          <Outlet />
        </Content>
        <Footer className="DashboardLayout__footer">
          <div>copyright©{new Date().getFullYear()}</div>
        </Footer>
      </Layout>
    </Layout>
  );
}

export default DashboardLayout;
